import * as React from 'react'
import * as PropTypes from 'prop-types'
import { css, Rule } from 'glamor'

import { isElement, isKey } from '../../utils'
import { useTheme } from '../../theme'

import { Flex } from '../flex'

import getStyles from './styles'

const sizes = ['small', 'medium', 'large']

const SPACE = String.fromCharCode(160)

interface IDefinitionListProps {
  children?: React.ReactNode
  size?: 'small' | 'medium' | 'large'
  flush?: boolean
  style?: Rule
}

export function DefinitionList({ children, size, flush, style }: IDefinitionListProps) {
  const styles = getStyles(useTheme())
  const childCount = React.Children.toArray(children).filter(Boolean).length

  return (
    <Flex
      component="dl"
      flexWrap="wrap"
      row
      {...css({
        ...styles.dl.base,
        ...(flush ? styles.dl.flush : {}),
        ...style,
      })}
      width="100%"
    >
      {React.Children.map(children, (child, index) =>
        child && isElement(child) ? React.cloneElement(child, { size, last: index >= childCount - 2 }) : null
      )}
    </Flex>
  )
}

DefinitionList.propTypes = {
  children: PropTypes.node,
  size: PropTypes.oneOf(sizes),
  flush: PropTypes.bool,
  style: PropTypes.object,
}

DefinitionList.defaultProps = {
  size: 'medium',
}

interface IDefinitionListTermProps {
  children?: React.ReactNode
  size?: 'small' | 'medium' | 'large'
  last?: boolean
  style?: Rule
}

export function DefinitionListTerm({ children, last, size, style }: IDefinitionListTermProps) {
  const styles = getStyles(useTheme())
  const sizeKey = `size-${size}`
  return (
    <Flex
      component="dt"
      {...css({
        ...styles.dt.base,
        ...(last ? styles.dt.last : {}),
        ...((isKey(styles.dt, sizeKey) && styles.dt[sizeKey]) || {}),
        ...style,
      })}
      width="50%"
    >
      {children == null || children === '' ? SPACE : children}
    </Flex>
  )
}

DefinitionListTerm.propTypes = {
  children: PropTypes.node,
  last: PropTypes.bool,
  size: PropTypes.oneOf(sizes),
  style: PropTypes.object,
}

interface IDefinitionListDefinitionProps {
  children?: React.ReactNode
  size?: 'small' | 'medium' | 'large'
  last?: boolean
  style?: Rule
}

export function DefinitionListDefinition({ children, last, size, style }: IDefinitionListDefinitionProps) {
  const styles = getStyles(useTheme())
  const sizeKey = `size-${size}`
  return (
    <Flex
      alignItems="flex-end"
      component="dd"
      {...css({
        ...styles.dd.base,
        ...(last ? styles.dd.last : {}),
        ...((isKey(styles.dd, sizeKey) && styles.dd[sizeKey]) || {}),
        ...style,
      })}
      width="50%"
    >
      {children == null || children === '' ? SPACE : children}
    </Flex>
  )
}

DefinitionListDefinition.propTypes = {
  children: PropTypes.node,
  last: PropTypes.bool,
  size: PropTypes.oneOf(sizes),
  style: PropTypes.object,
}
